import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, Briefcase, CheckCircle, Home as HomeIcon, MapPin, MoreHorizontal, Save, Search } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../api/api";
import { useApp } from "../context/AppContext";

const emptyAddress = {
  ShortName: "",
  Address1: "",
  City: "",
  Division: "",
  State: "",
  Pincode: "",
  AreaCode: "",
  Latitude: "",
  Longitude: "",
};

const cleanAddressApiText = (value) => {
  const text = String(value ?? "").trim();
  return text === "-" ? "" : text;
};

const getAddressId = (address) =>
  Number(address?.AId || address?.Aid || address?.AID || address?.aId || address?.AddressId || address?.Id || address?.id || 0);

const getAddressIdentityKey = (address) =>
  [
    address?.ShortName,
    address?.Address1 || address?.Address || address?.AddressLine,
  ]
    .map((part) => cleanAddressApiText(part).toLowerCase())
    .join("|");

const getShortNameFormValues = (shortName = "") => {
  const cleanName = cleanAddressApiText(shortName);
  if (cleanName === "Home" || cleanName === "Office") {
    return { shortNameType: cleanName, shortNameOther: "" };
  }
  return { shortNameType: cleanName ? "Other" : "Home", shortNameOther: cleanName };
};

export default function AddAdditionalAddress() {
  const { user, setUser } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const editAddress = location.state?.mode === "edit" ? location.state?.address : null;
  const isEditMode = !!editAddress;
  const [areas, setAreas] = useState([]);
  const [form, setForm] = useState(emptyAddress);
  const [pincodeToVerify, setPincodeToVerify] = useState("");
  const [isPincodeAllowed, setIsPincodeAllowed] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [isCheckingPincode, setIsCheckingPincode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [areaSearch, setAreaSearch] = useState("");
  const [shortNameType, setShortNameType] = useState("Home");
  const [shortNameOther, setShortNameOther] = useState("");
  const [filteredAreas, setFilteredAreas] = useState([]);
  const [showAreaList, setShowAreaList] = useState(false);
  const areaRef = useRef(null);

  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const res = await api.GetCustAreaList();
        const list = res?.GetCustAreaListjs?.Table || [];
        setAreas(list);
        setFilteredAreas(list);
      } catch (err) {
        console.error("Failed to fetch area list:", err);
      }
    };

    fetchAreas();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (areaRef.current && !areaRef.current.contains(event.target)) {
        setShowAreaList(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!editAddress) return;

    const editPincode = cleanAddressApiText(editAddress.Pincode);
    const editShortName = cleanAddressApiText(editAddress.ShortName);
    const shortNameValues = getShortNameFormValues(editShortName);
    setForm({
      ShortName: editShortName,
      Address1: cleanAddressApiText(editAddress.Address1 || editAddress.Address),
      City: cleanAddressApiText(editAddress.City || editAddress.Area),
      Division: cleanAddressApiText(editAddress.Division || editAddress.Route),
      State: cleanAddressApiText(editAddress.State),
      Pincode: editPincode,
      AreaCode: cleanAddressApiText(editAddress.AreaCode),
      Latitude: cleanAddressApiText(editAddress.Latitude),
      Longitude: cleanAddressApiText(editAddress.Longitude),
      AId: Number(editAddress.AId) || 0,
    });
    setShortNameType(shortNameValues.shortNameType);
    setShortNameOther(shortNameValues.shortNameOther);
    setAreaSearch(cleanAddressApiText(editAddress.City || editAddress.Area));
    setPincodeToVerify(editPincode);
    setIsPincodeAllowed(true);
    setMessage("");
    setMessageType("");
  }, [editAddress]);

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleAreaSearchChange = (event) => {
    const value = event.target.value;
    setAreaSearch(value);
    setForm((prev) => ({
      ...prev,
      City: value,
      Division: "",
      State: "",
      AreaCode: "",
    }));

    const query = value.trim().toLowerCase();
    const filtered = query
      ? areas.filter((area) => {
          const city = String(area?.city || "").toLowerCase();
          const division = String(area?.Division || "").toLowerCase();
          return city.includes(query) || division.includes(query);
        })
      : areas;

    setFilteredAreas(filtered);
    setShowAreaList(true);
  };

  const handleSelectArea = (area) => {
    const city = cleanAddressApiText(area?.city);
    const division = cleanAddressApiText(area?.Division);

    setAreaSearch(city);
    setForm((prev) => ({
      ...prev,
      City: city,
      Division: division,
      State: area?.State || prev.State,
      AreaCode: area?.AreaCode || prev.AreaCode,
    }));
    setShowAreaList(false);
  };

  const verifyPincode = async () => {
    const cleaned = pincodeToVerify.trim();
    setMessage("");
    setMessageType("");
    setIsPincodeAllowed(false);

    if (!/^\d{6}$/.test(cleaned)) {
      setMessage("Enter a valid 6-digit pincode.");
      setMessageType("error");
      return;
    }

    try {
      setIsCheckingPincode(true);
      const res = await api.CheckPincode(cleaned);
      const row = res?.PincodeStatus?.Table?.[0];
      const status = String(row?.Status || "").toLowerCase();
      const allowed = status === "allowed" || status === "active" || row?.PinAllowed === 1;

      setIsPincodeAllowed(allowed);
      setMessage(
        allowed
          ? "Delivery available. Enter the additional address details."
          : row?.Message || "Delivery is not available for this pincode."
      );
      setMessageType(allowed ? "success" : "error");

      if (allowed) {
        updateForm("Pincode", row?.Pincode || cleaned);
        setFilteredAreas(areas);
      }
    } catch (err) {
      console.error("CheckPincode failed:", err);
      setMessage("Server error while checking pincode. Please try again.");
      setMessageType("error");
    } finally {
      setIsCheckingPincode(false);
    }
  };

  const saveAddress = async (event) => {
    event.preventDefault();
    const shortName = shortNameType === "Other" ? cleanAddressApiText(shortNameOther) : shortNameType;

    if (!shortName || !form.Address1 || !form.City || !form.Division || (!isEditMode && !form.Pincode)) {
      setMessage(isEditMode ? "Fill short name, address, and route." : "Fill short name, address, route, and pincode.");
      setMessageType("error");
      return;
    }

    const addressToAdd = {
      Vid: user?.Vid,
      ShortName: shortName,
      Address1: cleanAddressApiText(form.Address1),
      Address2: "",
      City: cleanAddressApiText(form.City),
      Division: cleanAddressApiText(form.Division),
      State: cleanAddressApiText(form.State),
      Pincode: cleanAddressApiText(form.Pincode || pincodeToVerify),
      AreaCode: form.AreaCode,
      Latitude: cleanAddressApiText(form.Latitude),
      Longitude: cleanAddressApiText(form.Longitude),
      AId: Number(form.AId) || 0,
      IsCodAvail: 1,
      isSessionAddress: true,
    };
    const addressAId = Number(addressToAdd.AId) || 0;

    const payload = {
      User: user?.Vcode || user?.mobile || user?.name || "Customer",
      Vid: user?.Vid || 0,
      Items: [
        {
          ItemId: 0,
          ShortName: addressToAdd.ShortName,
          Address: addressToAdd.Address1,
          Area: addressToAdd.City,
          Item_Type: " ",
          Route: addressToAdd.Division,
          Pincode: addressToAdd.Pincode,
          Latitude: addressToAdd.Latitude,
          Longitude: addressToAdd.Longitude,
          AId: addressAId,
        },
      ],
      Action: addressAId > 0 ? "update" : "insert",
    };

    try {
      setIsSaving(true);
      await api.CustomerMobileAddAPI(payload);

      const sessionAddresses = JSON.parse(sessionStorage.getItem("cartAdditionalAddresses") || "[]");
      const updatedIdentityKey = getAddressIdentityKey(addressToAdd);
      const originalIdentityKey =
        isEditMode && !addressAId && editAddress
          ? getAddressIdentityKey({
              ShortName: cleanAddressApiText(editAddress.ShortName),
              Address1: cleanAddressApiText(editAddress.Address1 || editAddress.Address),
            })
          : null;
      const updatedSessionAddresses = isEditMode
        ? [
            ...sessionAddresses.filter((address) =>
              addressAId
                ? getAddressId(address) !== addressAId
                : getAddressIdentityKey(address) !== originalIdentityKey
            ),
            addressToAdd,
          ]
        : [
            ...sessionAddresses.filter((address) => getAddressIdentityKey(address) !== updatedIdentityKey),
            addressToAdd,
          ];
      sessionStorage.setItem("cartAdditionalAddresses", JSON.stringify(updatedSessionAddresses));

      const selectedAddress = {
        selectedKey: `additional-${addressToAdd.ShortName || updatedSessionAddresses.length}-${updatedSessionAddresses.length - 1}`,
        AId: addressToAdd.AId,
        ShortName: addressToAdd.ShortName,
        AddressLine: addressToAdd.Address1,
        City: addressToAdd.City,
        Division: addressToAdd.Division,
        isPrimary: false,
      };
      const primaryAddressLine =
        user?.Address1 || (!user?.selectedAdditionalAddress ? user?.address : "") || "";
      const primaryLandmark =
        user?.Address2 || (!user?.selectedAdditionalAddress ? user?.landmark : "") || "";
      const primaryCity =
        user?.City || user?.city || (!user?.selectedAdditionalAddress ? user?.area : "") || "";
      const primaryDivision =
        user?.Division || (!user?.selectedAdditionalAddress ? user?.division : "") || "";
      const updatedUser = {
        ...user,
        Address1: primaryAddressLine,
        Address2: primaryLandmark,
        City: primaryCity,
        Division: primaryDivision,
        address: selectedAddress.AddressLine,
        landmark: "",
        area: selectedAddress.City || user?.area || "",
        division: selectedAddress.Division || user?.division || "",
        IsCodAvail: addressToAdd.IsCodAvail,
        selectedAdditionalAddress: selectedAddress,
      };

      setUser(updatedUser);
      sessionStorage.setItem("user", JSON.stringify(updatedUser));
      localStorage.setItem("user", JSON.stringify(updatedUser));
      navigate("/cart");
    } catch (err) {
      console.error("Failed to save additional address:", err);
      setMessage("Failed to save address. Please try again.");
      setMessageType("error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="w-full max-w-md p-6 mx-auto bg-white shadow-lg animate-fade-in md:max-w-2xl md:p-8 rounded-2xl">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <p className="text-xs font-bold tracking-wide text-red-500 uppercase">Delivery Address</p>
          <h2 className="text-2xl font-bold text-red-600">
            {isEditMode ? "Edit Additional Address" : "Add Additional Address"}
          </h2>
        </div>
        <button
          type="button"
          onClick={() => navigate("/cart")}
          className="inline-flex items-center justify-center w-10 h-10 text-gray-600 transition-colors rounded-full hover:bg-gray-100"
          aria-label="Back to cart"
        >
          <ArrowLeft size={20} />
        </button>
      </div>

      <form onSubmit={saveAddress} className="space-y-3">
        {!isEditMode && (
        <div className="grid grid-cols-1 gap-2 p-3 border border-gray-200 rounded-lg bg-gray-50 sm:grid-cols-[1fr_auto]">
          <input
            type="tel"
            inputMode="numeric"
            maxLength={6}
            value={pincodeToVerify}
            onChange={(e) => setPincodeToVerify(e.target.value.replace(/\D/g, "").slice(0, 6))}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                verifyPincode();
              }
            }}
            placeholder="Enter 6-digit pincode"
            className="w-full p-3 bg-white border rounded-md focus:outline-none focus:ring-2 focus:ring-red-200"
          />
          <button
            type="button"
            onClick={verifyPincode}
            disabled={isCheckingPincode}
            className="px-5 py-3 font-bold text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-60"
          >
            {isCheckingPincode ? "Checking..." : "Verify"}
          </button>
        </div>
        )}

        {message && (
          <p className={`px-3 py-2 text-sm font-semibold border rounded-md ${
            messageType === "success"
              ? "text-green-700 bg-green-50 border-green-100"
              : "text-red-600 bg-red-50 border-red-100"
          }`}>
            {messageType === "success" && <CheckCircle className="inline-block mr-1" size={15} />}
            {message}
          </p>
        )}

        {isPincodeAllowed && (
          <>
            <textarea
              rows="3"
              value={form.Address1}
              onChange={(e) => updateForm("Address1", e.target.value)}
              className="w-full p-3 border rounded-md"
              placeholder="Address"
            />
            <div className="relative" ref={areaRef}>
              <div className="relative">
                <Search className="absolute text-gray-400 -translate-y-1/2 left-3 top-1/2" size={17} />
                <input
                  type="text"
                  value={areaSearch}
                  onChange={handleAreaSearchChange}
                  onFocus={() => {
                    setFilteredAreas(areaSearch.trim() ? filteredAreas : areas);
                    setShowAreaList(true);
                  }}
                  className="w-full py-3 pl-10 pr-3 bg-white border rounded-md focus:outline-none focus:ring-2 focus:ring-red-200"
                  placeholder="Search area or route"
                />
              </div>
              {showAreaList && (
                <div className="absolute z-20 w-full mt-1 overflow-hidden bg-white border border-gray-200 rounded-lg shadow-xl">
                  <div className="overflow-y-auto max-h-56">
                    {filteredAreas.length > 0 ? (
                      filteredAreas.map((area, index) => (
                        <button
                          type="button"
                          key={`${area.city}-${area.Division}-${index}`}
                          onClick={() => handleSelectArea(area)}
                          className="flex items-start w-full gap-2 px-3 py-2 text-left transition-colors border-b border-gray-100 last:border-b-0 hover:bg-red-50"
                        >
                          <span className="inline-flex items-center justify-center flex-shrink-0 w-7 h-7 mt-0.5 text-red-600 bg-red-50 border border-red-100 rounded-md">
                            <MapPin size={14} />
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm font-bold text-gray-900">{area.city || "Unnamed Area"}</span>
                            <span className="block text-xs font-semibold text-gray-500">{area.Division || "Route not available"}</span>
                          </span>
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-4 text-sm font-semibold text-center text-gray-500">
                        No matching areas found.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <input value={form.Division} readOnly className="w-full p-3 bg-gray-100 border rounded-md" placeholder="Route" />
            {/* <input
              value={form.State}
              onChange={(e) => updateForm("State", e.target.value)}
              className="w-full p-3 border rounded-md"
              placeholder="State"
            /> */}
            <input value={form.Pincode} readOnly className="w-full p-3 bg-gray-100 border rounded-md" placeholder="Pincode" />
            {/* <input
              value={form.AreaCode}
              onChange={(e) => updateForm("AreaCode", e.target.value.replace(/\D/g, ""))}
              className="w-full p-3 border rounded-md"
              placeholder="Area Code"
            /> */}
            <div className="pt-1 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                {[
                  { label: "Home", icon: HomeIcon },
                  { label: "Office", icon: Briefcase },
                  { label: "Other", icon: MoreHorizontal },
                ].map(({ label, icon: Icon }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => {
                      setShortNameType(label);
                      if (label !== "Other") {
                        updateForm("ShortName", label);
                      }
                    }}
                    className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold border rounded-full transition-colors ${
                      shortNameType === label
                        ? "text-white bg-red-500 border-red-500"
                        : "text-gray-700 bg-white border-gray-200 hover:bg-red-50 hover:border-red-200"
                    }`}
                  >
                    <Icon size={13} />
                    {label}
                  </button>
                ))}
              </div>
              {shortNameType === "Other" && (
                <input
                  value={shortNameOther}
                  onChange={(e) => {
                    setShortNameOther(e.target.value);
                    updateForm("ShortName", e.target.value);
                  }}
                  className="w-full p-3 border rounded-md"
                  placeholder="Enter address type"
                  required
                />
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => navigate("/cart")}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-60"
              >
                <Save size={16} />
                {isSaving ? "Saving..." : isEditMode ? "Update Address" : "Save Address"}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
